"use client";
import "./funds_hero.scss";
import { useFunds } from "@/contexts/funds-context";
import { BackNavigation } from "@/elements/back_navigation/back_navigation";
import { Container } from "@/elements/container/container";
import { Dropdown } from "@/elements/dropdown/dropdown";
import { i18n } from "@/model/i18n";
import { Routes } from "@/model/routes";
import { type ReactElement } from "react";
import { Stack } from "@/elements/stack/stack";
import { CardSlider } from "@/components/card_slider/card_slider";
import { FundCard } from "@/components/fund_card/fund_card";
import { Text } from "@/elements/text/text";

export function FundsHero(): ReactElement | null {
  const {
    allFunds,
    selectedFund,
    selectedTranche,
    loading,
    error,
    setSelectedFund,
    setSelectedTranche,
  } = useFunds();
  const current = allFunds.findIndex((f) => f.id === selectedFund?.id);
  const previous = allFunds[current - 1];
  const next = allFunds[current + 1];

  function setCurrent(index: number): void {
    const fund = allFunds[index];

    setSelectedFund(fund.id);
    window.history.pushState(null, "", `/solutions/funds/${fund.id}`);
  }

  const displayData = !loading && !error;

  return (
    <header className="funds-hero">
      <Stack size={40} tabletSize={56}>
        <Container long>
          <Stack
            size={24}
            line="tablet"
            justifyTablet="space-between"
            alignTablet="center"
          >
            <BackNavigation
              href={Routes.solutions}
              label={i18n.t("pages.funds.solutions")}
              light
            />

            {displayData && (
              <div className="funds-hero__tranche-selector">
                <Dropdown
                  id="funds-hero-tranche-dropdown"
                  options={
                    selectedFund?.tranches.map((tranche) => ({
                      label: tranche.name,
                      value: tranche.id.toString(),
                    })) || []
                  }
                  value={String(selectedTranche?.id)}
                  onChange={(value) => setSelectedTranche(Number(value))}
                  label={i18n.t("pages.funds.select_an_option")}
                />
              </div>
            )}
          </Stack>
        </Container>
        {displayData ? (
          <CardSlider
            noActive
            value={current}
            onChange={setCurrent}
            previousLabel={previous?.name}
            nextLabel={next?.name}
          >
            {allFunds?.map((fund, index) => {
              return (
                <FundCard
                  key={fund.id}
                  title={selectedTranche?.name || fund.name}
                  type={fund.fundTypeName as typeof FundCard.prototype.type}
                  riskClass={fund.sriClass}
                  volumen={fund.volume}
                  isin={selectedTranche?.isin || null}
                  nav={selectedTranche?.nav || 0}
                  stand={selectedTranche?.modifiedAt || null}
                  active={current === index}
                  equityQuotaPerc={fund.equityQuotaPerc}
                  sfdrClassification={fund.sfdrClassification}
                  wkn={selectedTranche?.wkn || null}
                />
              );
            })}
          </CardSlider>
        ) : (
          <div className="funds-hero__fallback">
            {loading && (
              <Text type="body-regular" color="neutral-on-surface">
                {i18n.t("pages.funds.loading_funds_data")}
              </Text>
            )}
            {error && (
              <Text type="body-regular" color="neutral-on-surface">
                {i18n.t("pages.funds.error_loading_funds_data")}
              </Text>
            )}
          </div>
        )}
      </Stack>
    </header>
  );
}
